<script setup lang="ts">
/**
 * Мои задачи — макет «Мои задачи v2», фрейм 345-20518: три чипа с серверными
 * счётчиками, поиск, секции по проектам и строки со сроком и пилюлей статуса.
 *
 * Список сводный: он единственный экран, где рядом стоят задачи разработки и
 * продаж. Поэтому строка показывает ключ с префиксом проекта, а группа — сам
 * проект: без него «214» и «1421» ничем не отличаются.
 *
 * Данные v2 (DEV-2045…2047): чипы считает сервер, секции приходят из
 * ListIssueGroups с честными счётчиками и догрузкой, строка на десктопе
 * раскрывает peek-панель. Прежняя реализация выкачивала все страницы дважды.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { useDebounceFn } from '@vueuse/core'
import { AlertTriangle, ArrowDownUp, CheckSquare, Search } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'
import ContextMenu from '@/components/ContextMenu.vue'
import FilterChip from '@/components/FilterChip.vue'
import UndoToast from '@/components/UndoToast.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import { useAuthStore } from '@/features/auth/store'
import { setWatcher } from '@/features/issue/api'
import IssuePeek from '@/features/issue/components/IssuePeek.vue'
import {
  closeIssue,
  moveIssue,
  setAssignee,
  setDueDate,
  setPriority,
} from '@/features/issues/api'
import {
  dueFromChoice,
  isPriorityChoice,
  issueMenuItems,
  type DueChoice,
} from '@/features/issues/menu'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { formatDueDate } from '@/features/issues/format'
import {
  isOverdue,
  listMine,
  listMyGroups,
  loadColumns,
  myCounts,
  type MyCounts,
  type MyFilter,
  type MyGroupSection,
  type MyIssue,
  type MySortField,
} from '@/features/my/api'
import { useProjectsStore } from '@/features/projects/store'

import type { ProjectColumn } from '@/features/projects/api'

const { t, locale } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const projects = useProjectsStore()
const isMobile = useIsMobile()

const groups = ref<MyGroupSection[]>([])
const counts = ref<MyCounts>({ assigned: 0, watchedOnly: 0, overdue: 0 })
const columns = ref<Record<string, ProjectColumn[]>>({})
const filter = ref<MyFilter>('assigned')
const isLoading = ref(false)
const error = ref('')

/**
 * Отбор и порядок уходят на сервер: личный список у человека с пятью проектами
 * — это сотни задач, и клиентский отбор показывал бы «ничего не найдено» там,
 * где искомое просто не поместилось в страницу.
 */
const projectFilter = ref<string[]>([])
const statusFilter = ref<string[]>([])
const dueFilter = ref<string[]>([])
// Свежие сверху: свой список читают с того, что завели последним, а ключ на
// сервере сортируется по номеру задачи, не по строке (DEV-2076).
const sortBy = ref<MySortField>('key')
/** Направление — своя кнопка: раньше sortDesc был объявлен и никуда не уходил */
const sortDesc = ref(true)
const search = ref('')

/** Окна срока: «на сегодня» и «на неделю» — два вопроса, которые задают чаще всего */
const DUE_WINDOWS: Record<string, number> = { today: 0, week: 7 }

const STATUS_CATEGORIES = [
  'STATUS_CATEGORY_OPEN',
  'STATUS_CATEGORY_IN_PROGRESS',
  'STATUS_CATEGORY_DONE',
] as const

const SORT_FIELDS: MySortField[] = ['due_date', 'priority', 'updated_at', 'key']

// Чипы — серверные числа (DEV-2035): длина привезённого куска врала бы на
// всём, что осталось за страницей.
const chips = computed(() => [
  { value: 'assigned' as const, label: t('my.chips.assigned'), count: counts.value.assigned },
  { value: 'watching' as const, label: t('my.chips.watching'), count: counts.value.watchedOnly },
  { value: 'overdue' as const, label: t('my.chips.overdue'), count: counts.value.overdue },
])

const projectOf = (group: MyGroupSection) =>
  projects.items.find((entry) => entry.id === group.key || entry.key === group.projectKey)

const projectName = (group: MyGroupSection) => {
  const project = projectOf(group)

  return project ? `${project.name} · ${project.key}` : group.projectKey || group.key
}

const projectColor = (group: MyGroupSection) =>
  projectOf(group)?.color || 'var(--color-primary-light)'

/** Что уходит на сервер вместе со списком */
const query = computed(() => ({
  projectIds: projectFilter.value,
  statusCategories: statusFilter.value,
  dueBefore: dueBefore.value,
  search: search.value,
  sortBy: sortBy.value,
  sortDesc: sortDesc.value,
}))

/**
 * Граница срока — конец выбранного окна: «на сегодня» это всё, что должно быть
 * сделано до завтрашнего утра, а не до текущей минуты.
 */
const dueBefore = computed(() => {
  if (!dueFilter.value.length) return undefined

  const days = Math.max(...dueFilter.value.map((value) => DUE_WINDOWS[value] ?? 0))
  const now = new Date()
  const edge = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days + 1)

  return edge.toISOString()
})

const projectOptions = computed(() =>
  projects.items.map((project) => ({ value: project.id, label: `${project.name} · ${project.key}` })),
)

const statusOptions = computed(() =>
  STATUS_CATEGORIES.map((value) => ({ value, label: t(`my.filters.status.${value}`) })),
)

const dueOptions = computed(() =>
  Object.keys(DUE_WINDOWS).map((value) => ({ value, label: t(`my.filters.due.${value}`) })),
)

const sortOptions = computed(() =>
  SORT_FIELDS.map((value) => ({ value, label: t(`my.sort.${value}`) })),
)

const columnOf = (issue: MyIssue) =>
  columns.value[issue.projectId]?.find((column) => column.id === issue.columnId)

/** Срок: у ближних дней слова, у дальних — дата, как в макете. */
const dueLabel = (issue: MyIssue) => {
  if (!issue.dueDate) return '—'

  const due = new Date(issue.dueDate)
  const now = new Date()
  const days = Math.round(
    (new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime() -
      new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) /
      86_400_000,
  )

  if (days === 0) return t('my.due.today')
  if (days === 1) return t('my.due.tomorrow')

  return formatDueDate(issue.dueDate, locale.value)
}

const load = async () => {
  isLoading.value = true
  error.value = ''

  try {
    // Один заход: счётчики и секции — два запроса вместо полной двойной
    // выкачки (DEV-2045).
    const [nextCounts, nextGroups] = await Promise.all([
      myCounts(query.value),
      listMyGroups(filter.value, query.value),
    ])

    counts.value = nextCounts
    groups.value = nextGroups
    columns.value = await loadColumns(nextGroups.flatMap((group) => group.key))
  } catch {
    error.value = t('my.error')
  } finally {
    isLoading.value = false
  }
}

/**
 * «Показать ещё» секции: группа перечитывается на больший размер — страницы
 * сервера выровнены по perPage, «с 10-й по 35-ю» им не спрашивается.
 */
const loadMoreGroup = async (key: string) => {
  const group = groups.value.find((entry) => entry.key === key)

  if (!group) return

  try {
    const size = Math.min(group.issues.length + 25, 100)
    const result = await listMine(filter.value, { ...query.value, projectIds: [key] }, 1, size)

    group.issues = result.issues
    group.total = result.total
  } catch (failure) {
    toast.error(errorMessage(failure, t('my.error')))
  }
}

const pushSearch = useDebounceFn(() => void load(), 400)

watch([filter, projectFilter, statusFilter, dueFilter, sortBy, sortDesc], () => void load(), {
  deep: true,
})

onMounted(async () => {
  await projects.ensureLoaded()
  await load()
})

/**
 * Peek на десктопе (DEV-2047): быстро пролистать несколько задач подряд, не
 * теряя список. На телефоне — переход на страницу, панель там не помещается.
 */
const openRow = (event: MouseEvent, issue: MyIssue) => {
  if (isMobile.value) return

  event.preventDefault()
  void router.push({ query: { ...route.query, issue: issue.key } })
}

const onPeekFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('issue.saveFailed')))
}

// ─────────────────── контекст-меню строки (DEV-2051/2052) ───────────────────

const auth = useAuthStore()
const menuRef = ref<InstanceType<typeof ContextMenu> | null>(null)
const menuTarget = ref<MyIssue | null>(null)

/** Статусы — колонки проекта строки: они уже загружены для пилюль */
const menuItems = computed(() =>
  issueMenuItems(t, menuTarget.value ? (columns.value[menuTarget.value.projectId] ?? []) : []),
)

const onRowMenu = (event: MouseEvent, issue: MyIssue) => {
  event.preventDefault()
  menuTarget.value = issue
  menuRef.value?.open(event.clientX, event.clientY)
}

const undo = ref({ message: '', issueId: '', columnId: '' })

const onUndoClose = async () => {
  if (!undo.value.issueId || !undo.value.columnId) return

  try {
    await moveIssue(undo.value.issueId, undo.value.columnId)
    await load()
  } catch (failure) {
    toast.error(errorMessage(failure, t('issue.saveFailed')))
  }
}

const onMenuSelect = async (key: string, child?: string) => {
  const issue = menuTarget.value

  if (!issue) return

  try {
    switch (key) {
      case 'self':
        if (auth.user) await setAssignee(issue.id, auth.user.id)

        break
      case 'status':
        if (child) await moveIssue(issue.id, child)

        break
      case 'priority':
        if (child && isPriorityChoice(child)) await setPriority(issue.id, child)

        break
      case 'due':
        if (child) await setDueDate(issue.id, dueFromChoice(child as DueChoice))

        break
      case 'close': {
        const previous = issue.columnId
        await closeIssue(issue.id)

        undo.value = {
          message: t('menu.closed', { key: issue.key }),
          issueId: issue.id,
          columnId: previous,
        }

        break
      }
      case 'watch':
        await setWatcher(issue.id, true, auth.user?.id)
        toast.success(t('menu.watching', { key: issue.key }))

        return
      case 'copy-key':
        await navigator.clipboard.writeText(issue.key)

        return
      case 'copy-link':
        await navigator.clipboard.writeText(`${window.location.origin}/issue/${issue.key}`)

        return
      case 'open-tab':
        window.open(`/issue/${issue.key}`, '_blank')

        return
    }

    // Список без оптимистики: после правки честный перезапрос.
    await load()
  } catch (failure) {
    toast.error(errorMessage(failure, t('issue.saveFailed')))
  }
}
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.myTasks') }]">
    <FilterChip
      v-model="projectFilter"
      :label="t('my.filters.project')"
      :options="projectOptions"
      :empty-text="t('toolbar.filters.empty')"
    />
    <FilterChip
      v-model="statusFilter"
      :label="t('my.filters.statusLabel')"
      :options="statusOptions"
      :empty-text="t('toolbar.filters.empty')"
    />
    <FilterChip
      v-model="dueFilter"
      :label="t('my.filters.dueLabel')"
      :options="dueOptions"
      :empty-text="t('toolbar.filters.empty')"
    />

    <!-- Порядок — выбор из одного значения: чип отдаёт последнее нажатое. -->
    <FilterChip
      :model-value="[sortBy]"
      :label="t('my.sort.label')"
      :options="sortOptions"
      :empty-text="t('toolbar.filters.empty')"
      @update:model-value="sortBy = ($event[$event.length - 1] as MySortField) ?? 'due_date'"
    />

    <!-- Направление раньше было объявлено и не пробрасывалось (DEV-2046). -->
    <button
      type="button"
      class="my__dir"
      :title="t('my.sort.direction')"
      :aria-label="t('my.sort.direction')"
      :aria-pressed="sortDesc"
      @click="sortDesc = !sortDesc"
    >
      <ArrowDownUp :size="14" />
      {{ sortDesc ? '↓' : '↑' }}
    </button>

    <label class="my__search">
      <Search :size="14" />
      <input
        v-model="search"
        type="search"
        :placeholder="t('toolbar.search')"
        :aria-label="t('toolbar.search')"
        @input="pushSearch"
        @keydown.enter.prevent="load"
      />
    </label>
  </TrackerToolbar>

  <div class="my">
    <div class="my__chips">
      <button
        v-for="chip in chips"
        :key="chip.value"
        type="button"
        class="my__chip"
        :class="{ 'my__chip--active': filter === chip.value }"
        :aria-pressed="filter === chip.value"
        @click="filter = chip.value"
      >
        {{ chip.label }} · {{ chip.count }}
      </button>
    </div>

    <div v-if="isLoading && !groups.length" class="my__skeleton" />

    <TrackerEmptyState
      v-else-if="error"
      :icon="AlertTriangle"
      :title="t('my.error')"
      :description="t('my.errorHint')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="load">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!groups.length"
      :icon="CheckSquare"
      :title="t(`my.empty.${filter}.title`)"
      :description="t(`my.empty.${filter}.description`)"
    />

    <template v-else>
      <section v-for="group in groups" :key="group.key" class="my__group">
        <h2 class="my__group-head">
          <span class="my__dot" :style="{ backgroundColor: projectColor(group) }" />
          {{ projectName(group) }}
          <span class="my__group-count">{{ group.total }}</span>
        </h2>

        <div class="my__table">
          <RouterLink
            v-for="issue in group.issues"
            :key="issue.id"
            class="mrow"
            :to="`/issue/${issue.key}`"
            @click="openRow($event, issue)"
            @contextmenu="onRowMenu($event, issue)"
          >
            <IssueTypeIcon class="mrow__icon" :type="issue.type" />
            <span class="mrow__key">{{ issue.key }}</span>
            <span class="mrow__title">{{ issue.title }}</span>
            <span class="mrow__due" :class="{ 'mrow__due--overdue': isOverdue(issue) }">
              {{ dueLabel(issue) }}
            </span>
            <span
              v-if="columnOf(issue)"
              class="mrow__status"
              :style="{
                color: columnOf(issue)?.color,
                backgroundColor: `color-mix(in srgb, ${columnOf(issue)?.color} 12%, transparent)`,
              }"
            >
              {{ columnOf(issue)?.name }}
            </span>
          </RouterLink>

          <button
            v-if="group.issues.length < group.total"
            type="button"
            class="my__more"
            @click="loadMoreGroup(group.key)"
          >
            {{ t('issues.list.showMore', { count: group.total - group.issues.length }) }}
          </button>
        </div>
      </section>
    </template>
  </div>

  <ContextMenu ref="menuRef" :items="menuItems" @select="onMenuSelect" />

  <UndoToast :message="undo.message" :action-label="t('menu.undo')" @action="onUndoClose" />

  <IssuePeek v-if="!isMobile" @failed="onPeekFailed" />
</template>

<style scoped>
.my {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  padding: 28px 48px;
}

.my__chips {
  display: flex;
  gap: 8px;
}

.my__chip {
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.my__chip--active {
  border-color: var(--color-primary);
  background: var(--color-primary-tint);
  color: var(--color-primary-hover);
}

.my__dir {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.my__dir:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

.my__search {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 200px;
  min-width: 120px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-muted);
}

.my__search input {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  outline: none;
}

.my__group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.my__group-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.my__group-count {
  color: var(--color-text-muted);
}

.my__dot {
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
}

.my__table {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.my__more {
  height: 40px;
  padding: 0 16px;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: pointer;
}

.my__more:hover {
  background: var(--color-bg-muted);
}

.my__skeleton {
  height: 320px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.mrow {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
  color: inherit;
  text-decoration: none;
}

.mrow:last-child {
  border-bottom: none;
}

.mrow:hover {
  background: var(--color-bg-muted);
}

.mrow__key {
  width: 88px;
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
}

.mrow__title {
  flex: 1;
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.mrow__due {
  width: 76px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  text-align: right;
}

/* Просроченный срок красный: это единственное, ради чего на колонку смотрят. */
.mrow__due--overdue {
  color: var(--color-danger);
}

.mrow__status {
  min-width: 76px;
  padding: 2px 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-body-sm);
  text-align: center;
  white-space: nowrap;
}

/* Телефон — макет 28:107: строка становится двухстрочной карточкой. Ключ, срок
   и статус уходят наверх, название занимает вторую строку целиком: в 390
   пикселей одной строкой оно обрезается на третьем слове. */
@media (max-width: 768px) {
  .my {
    gap: 12px;
    padding: 16px 12px;
  }

  .my__chips {
    overflow-x: auto;
    margin: 0 -12px;
    padding: 0 12px;
    scrollbar-width: none;
  }

  .my__chip {
    flex-shrink: 0;
    height: 36px;
  }

  .mrow {
    display: grid;
    grid-template-areas:
      'icon key due status'
      'title title title title';
    grid-template-columns: auto auto 1fr auto;
    align-items: center;
    gap: 4px 8px;
    height: auto;
    padding: 12px 14px;
  }

  .mrow__icon {
    grid-area: icon;
  }

  .mrow__key {
    grid-area: key;
    width: auto;
  }

  .mrow__due {
    grid-area: due;
    width: auto;
    text-align: right;
  }

  .mrow__status {
    grid-area: status;
    min-width: 0;
  }

  .mrow__title {
    grid-area: title;
    white-space: normal;
  }
}
</style>
